import { useEffect, useImperativeHandle, useState } from "react";

import { LoadingProgressText } from "@bokio/designsystem/components/Loading";
import { useLoader } from "@bokio/hooks/useLoader/useLoader";
import { GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import * as m from "@bokio/mobile-web-shared/core/model/model";
import * as proxy from "@bokio/mobile-web-shared/services/api/proxy";
import { mergeClassNames } from "@bokio/utils/classes";

import { AddReceiptExtraPageButton } from "../AddReceiptExtraPageButton/AddReceiptExtraPageButton";
import { ReceiptDescription } from "./components/ReceiptDescription/ReceiptDescription";
import { ReceiptDescriptionEditor } from "./components/ReceiptDescriptionEditor/ReceiptDescriptionEditor";
import { ReceiptExtraPage } from "./components/ReceiptExtraPage/ReceiptExtraPage";
import { ReceiptMainPage } from "./components/ReceiptMainPage/ReceiptMainPage";
import { SalaryReceipt } from "./components/SalaryReceipt/SalaryReceipt";

import type { Ref } from "react";

import * as styles from "./receipt.scss";

import ReceiptType = m.Entities.ReceiptType;
interface ReceiptProps {
	receiptId: string;
	allowRotate?: boolean;
	allowDelete?: boolean;
	allowUpload?: boolean;
	allowEditDescription?: [true, () => void]; // the second arg is a callback that will be called when description changed.
	className?: string;
	onImageRef?: (imgRef: HTMLImageElement | null) => void;
	onDelete?: () => void;
	onReceiptLoad?: (receipt: m.Accounting.Viewmodels.ReceiptForView) => void;
	ref?: Ref<ReceiptFunctions>;
}

const loadReceiptEndpoint = ({ companyId, receiptId }: { companyId: string; receiptId: string }) => {
	return proxy.Accounting.ReceiptController.GetReceiptForView.Get(companyId, receiptId);
};

export interface ReceiptFunctions {
	loadExtraPages: () => void;
}

export const Receipt = ({ ref, ...props }: ReceiptProps) => {
	const { companyInfo } = useCompanyInfo();
	const [extraPages, setExtraPages] = useState<m.Accounting.Viewmodels.ReceiptExtraPageViewModel[]>([]);

	const { load, request } = useLoader({
		endpoint: loadReceiptEndpoint,
		onSuccess: data => {
			props.onReceiptLoad?.(data);
			setExtraPages(data.Extrapages);
		},
	});

	useEffect(() => {
		load({ companyId: companyInfo.Id, receiptId: props.receiptId });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyInfo.Id, props.receiptId]);

	const loadExtraPages = () => {
		load({ companyId: companyInfo.Id, receiptId: props.receiptId });
	};

	useImperativeHandle(ref, () => ({
		loadExtraPages,
	}));

	const generalLang = GeneralLangFactory();

	if (!request.data) {
		return <LoadingProgressText text={generalLang.LoadingImage} />;
	}

	const receipt = request.data;
	const salaryTypes = [ReceiptType.Salary, ReceiptType.SalaryTaxReport];
	const isSalaryType = receipt && salaryTypes.includes(receipt.ReceiptType);
	const isPdf = receipt && receipt.IsPdf;
	const version = receipt && receipt.Version;
	const description = receipt && receipt.Description;

	if (isSalaryType) {
		return <SalaryReceipt type={receipt.ReceiptType} externalId={receipt.ExternalId} receiptId={receipt.Id} />;
	}

	return (
		<div className={mergeClassNames(styles.receiptWrapper, props.className)}>
			{props.allowEditDescription ? (
				<ReceiptDescriptionEditor receipt={receipt} onChanged={props.allowEditDescription[1]} />
			) : (
				description && <ReceiptDescription description={description} />
			)}
			<ReceiptMainPage
				companyId={companyInfo.Id}
				id={props.receiptId}
				allowRotate={props.allowRotate}
				isPdf={isPdf || false}
				version={version}
				onImageRef={props.onImageRef}
				onDelete={props.onDelete}
			/>
			{extraPages &&
				extraPages.map(extraPage => (
					<ReceiptExtraPage
						companyId={companyInfo.Id}
						page={extraPage.PageNumber}
						extension={extraPage.Extension}
						key={extraPage.Id}
						pageId={extraPage.Id}
						allowRotate={props.allowRotate}
						allowDelete={props.allowDelete}
						isPdf={extraPage.IsPdf}
						pageVersion={version}
						onRemove={loadExtraPages}
					/>
				))}
			{props.allowUpload && (
				<div>
					<AddReceiptExtraPageButton receiptId={props.receiptId} pageAdded={loadExtraPages} />
				</div>
			)}
		</div>
	);
};
